import { ServerPost } from '../../../services/posts/api';
import { FeedPost } from '../../types/feed';

/**
 * Server post → the view model `FeedPostCard` renders.
 *
 * Lifted out of FeedPage on 2026-08-16 so the profiles can render the SAME
 * card as the feed. Before that, ProfilePage and PublicProfilePage each drew
 * their own read-only `<article>` with no engagement row at all: you could
 * like a post in the feed and see nothing on your own profile, because the
 * profile markup never referenced `likes` or `likedByMe`. The server has
 * always enriched both `/posts/mine` and `/user/:id/posts`; only the render
 * threw the numbers away.
 *
 * Pure and module-level on purpose — three callers now share exactly one
 * mapping, which is what stops the counts drifting apart again.
 */
export const toFeedPost = (
  post: ServerPost,
  author: { name: string; avatarUrl?: string },
): FeedPost => ({
  id: post.postId,
  author: { id: post.authorId, name: author.name, avatarUrl: author.avatarUrl },
  location: '',
  caption: post.text,
  // Two sets of expiring URLs from the media plane (live items only), because
  // the thumbnail is 256px wide: at natural size in the full-screen viewer it
  // renders as a postage stamp, and max-width/height only ever shrink.
  // Previews take the thumb, the viewer takes the full-size original.
  imageUrls: (post.media || [])
    .filter((item) => item.status === 'live')
    .map((item) => item.thumbUrl || item.url)
    .filter((url): url is string => Boolean(url)),
  imageFullUrls: (post.media || [])
    .filter((item) => item.status === 'live')
    .map((item) => item.url || item.thumbUrl)
    .filter((url): url is string => Boolean(url)),
  likes: post.likes ?? 0,
  comments: post.commentCount ?? 0,
  createdAt: post.createdAt,
  isLiked: post.likedByMe,
  shares: post.shares ?? 0,
  sharedByMe: post.sharedByMe,
  repostUnavailable: post.repostUnavailable,
  repost: post.repost
    ? {
        id: post.repost.postId,
        authorId: post.repost.authorId,
        caption: post.repost.text,
        createdAt: post.repost.createdAt,
        imageUrls: (post.repost.media || [])
          .filter((item) => item.status === 'live')
          .map((item) => item.thumbUrl || item.url)
          .filter((url): url is string => Boolean(url)),
        likes: post.repost.likes ?? 0,
        comments: post.repost.commentCount ?? 0,
      }
    : undefined,
});
